import {
  Alert,
  Button,
  Descriptions,
  DescriptionsProps,
  Modal,
  Space,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";
import {
  ADYEN_DOCUMENT_REASON,
  AdyenDocumentAcknowledgement,
  AdyenDocumentReason,
  AdyenDocumentStateRow,
  describeAdyenDocumentReason,
  normalizeAdyenDocumentReasonCode,
  ResubmitAdyenDocumentResponse,
} from "../../../../interfaces/adyenDocumentState.interface";
import { resubmitAdyenDocumentOnServer } from "../../businessesApi";
import { describeIdentityDocumentType } from "../Business-Editor-Modal/components/personIdentityDocumentValidation";
import { getFileDisplayName } from "../Business-Files-Modal/businessFiles.helpers";
import {
  acknowledgementForReason,
  AdyenFileAnnotationStatus,
  AdyenSubmissionSideReason,
  describeSidesToBeSubmitted,
  extractAdyenDocumentReason,
  isDownloadedFromAdyenOnly,
  requiredAcknowledgementFromRow,
} from "./adyenDocumentState.helpers";

type Acknowledgements = Partial<Record<AdyenDocumentAcknowledgement, boolean>>;

const ACKNOWLEDGEMENT_TITLE: Record<AdyenDocumentAcknowledgement, string> = {
  approvedOwner: "This owner is already approved by Adyen",
  merchantDocument: "This will deactivate the merchant's own document",
};

const panelStyle: React.CSSProperties = {
  border: "1px solid #f0f0f0",
  borderRadius: 8,
  padding: "8px 12px",
  marginTop: 8,
  background: "#fafafa",
};

export default function AdyenDocumentPanel({
  businessId,
  row,
  status,
  showControl,
  onSubmitted,
  otherReasons = [],
}: {
  businessId: string;
  row: AdyenDocumentStateRow;
  status: AdyenFileAnnotationStatus;
  showControl: boolean;
  onSubmitted: () => void;
  /** Set by a surface that renders a whole submission as this one panel. */
  otherReasons?: AdyenSubmissionSideReason[];
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<ResubmitAdyenDocumentResponse | null>(
    null
  );
  const [failure, setFailure] = useState<AdyenDocumentReason | null>(null);

  const sidesDescription = describeSidesToBeSubmitted(row.sidesToSubmit);
  const blockers = row.blockers ?? [];
  const isNotSubmittable = row.eligibility === "not_submittable";
  const isDownloadedOnly = isDownloadedFromAdyenOnly(row);
  const isDownloaded = row.adyenDocument?.origin === "downloaded";
  // The panel gates the control itself, so no caller can offer a resend of Adyen's own copy.
  const canOfferControl = showControl && !isNotSubmittable && !isDownloadedOnly;

  const submit = async (acknowledgements: Acknowledgements) => {
    setIsSubmitting(true);
    setFailure(null);
    try {
      const response = await resubmitAdyenDocumentOnServer({
        businessId,
        documentType: row.documentType,
        personId: row.personId,
        expectedSourceRef: row.sourceRef,
        acknowledgeApproved: acknowledgements.approvedOwner,
        acknowledgeMerchantDocument: acknowledgements.merchantDocument,
      });
      setResult(response);
      setIsSubmitting(false);
      onSubmitted();
    } catch (error) {
      const reason = extractAdyenDocumentReason(error);
      const retryAcknowledgement = acknowledgementForReason(reason);
      if (retryAcknowledgement && !acknowledgements[retryAcknowledgement]) {
        // The button stays busy until the confirm is answered, or it accepts a second click.
        confirmAcknowledgement({
          acknowledgement: retryAcknowledgement,
          reason,
          onConfirm: () =>
            submit({ ...acknowledgements, [retryAcknowledgement]: true }),
          onCancel: () => setIsSubmitting(false),
        });
        return;
      }
      setFailure(reason);
      setIsSubmitting(false);
    }
  };

  // Known from the read, so confirmed up front; the live gate arrives as a 409.
  const handleClick = () => {
    setResult(null);
    if (requiredAcknowledgementFromRow(row) === "approvedOwner") {
      confirmAcknowledgement({
        acknowledgement: "approvedOwner",
        reason: { code: row.reason ?? ADYEN_DOCUMENT_REASON.ownerApproved },
        onConfirm: () => submit({ approvedOwner: true }),
      });
      return;
    }
    submit({});
  };

  return (
    <div style={panelStyle}>
      <Space direction="vertical" size={4} style={{ width: "100%" }}>
        <Space wrap size={4}>
          {renderAdyenStateTag(row)}
          {status === "superseded" && <Tag>Superseded by a newer file</Tag>}
          {row.isAdyenSourced && !isDownloaded && (
            <Tag color="purple">Came from Adyen</Tag>
          )}
          {!row.hasUsableBytes && !isDownloadedOnly && (
            <Tag color="red">No usable file</Tag>
          )}
        </Space>

        <Descriptions
          size="small"
          column={1}
          colon={false}
          items={buildDescriptionItems(row)}
        />

        {/* Blockers are Adyen's verdict on the owner, so they show even for a synced document. */}
        {blockers.map((blocker, index) => (
          <Alert
            key={`${blocker.code ?? blocker.type ?? "blocker"}-${index}`}
            type="warning"
            showIcon
            message={describeAdyenDocumentReason(blocker)}
            description={
              row.blockersAttributable
                ? undefined
                : "Adyen did not say which owner this problem belongs to, so it may not be this one."
            }
          />
        ))}

        {row.reason && !isDownloadedOnly && (
          <Alert
            type={isNotSubmittable ? "error" : "warning"}
            showIcon
            message={describeAdyenDocumentReason({ code: row.reason })}
          />
        )}

        {otherReasons.map(({ documentType, reason }) => (
          <Alert
            key={`${documentType}-${reason}`}
            type="warning"
            showIcon
            message={`${getFileDisplayName(
              documentType
            )}: ${describeAdyenDocumentReason({ code: reason })}`}
          />
        ))}

        {canOfferControl && (
          <Button type="primary" loading={isSubmitting} onClick={handleClick}>
            {sidesDescription
              ? `Send to Adyen — ${sidesDescription}`
              : "Send to Adyen"}
          </Button>
        )}

        {failure && (
          <Alert
            type="error"
            showIcon
            message={
              isStale(failure) ? "A newer file now wins" : "Not submitted"
            }
            description={
              isStale(failure)
                ? "Nothing was sent. Reload the document state to see the file that would be submitted now."
                : describeAdyenDocumentReason(failure)
            }
            action={
              isStale(failure) ? (
                <Button size="small" onClick={onSubmitted}>
                  Reload
                </Button>
              ) : undefined
            }
          />
        )}

        {result && <SubmissionResult result={result} />}
      </Space>
    </div>
  );
}

const isStale = (reason: AdyenDocumentReason): boolean =>
  normalizeAdyenDocumentReasonCode(reason.code) ===
  ADYEN_DOCUMENT_REASON.staleSourceRef;

function buildDescriptionItems(
  row: AdyenDocumentStateRow
): DescriptionsProps["items"] {
  const items: NonNullable<DescriptionsProps["items"]> = [];

  if (row.adyenDocument) {
    items.push({
      key: "held",
      label: "Adyen holds",
      children: (
        <>
          <Typography.Text code>{row.adyenDocument.id}</Typography.Text>
          {row.adyenDocument.type ? ` · ${row.adyenDocument.type}` : ""}
          {row.adyenDocument.modificationDate
            ? ` · modified ${row.adyenDocument.modificationDate}`
            : ""}
        </>
      ),
    });
  }

  if (row.typeToSend) {
    items.push({
      key: "type",
      label: "Would be sent as",
      children: (
        <>
          {describeIdentityDocumentType(row.typeToSend)}
          {renderTypeMismatch(row)}
        </>
      ),
    });
  }

  const sidesToSubmit = row.sidesToSubmit ?? [];
  if (sidesToSubmit.length > 0) {
    items.push({
      key: "sides",
      label: "Sides that would be sent",
      children: sidesToSubmit.join(", "),
    });
  }

  if (row.file) {
    items.push({
      key: "file",
      label: "File that would be sent",
      children: [
        row.file.fileName || "unnamed",
        row.sourceWon,
        row.file.uploadedAt ? `uploaded ${row.file.uploadedAt}` : undefined,
      ]
        .filter(Boolean)
        .join(" · "),
    });
  }

  return items;
}

/** A document Adyen never received must not look like one it accepted. */
function renderAdyenStateTag(row: AdyenDocumentStateRow) {
  if (!row.adyenDocument) return <Tag color="red">Not at Adyen</Tag>;
  switch (row.adyenDocument.origin) {
    case "downloaded":
      return <Tag color="purple">Downloaded from Adyen</Tag>;
    case "uploaded":
      return <Tag color="green">Uploaded to Adyen</Tag>;
    case undefined:
      // A backend that predates `origin`: held, with no provenance to report.
      return <Tag color="green">At Adyen</Tag>;
    default:
      // An origin this build does not know: held, but do not claim a provenance.
      return <Tag>At Adyen</Tag>;
  }
}

/**
 * A declared type wins over OCR, so `typeToSend` already shows what the merchant
 * said. The detected type is the new information — it is what says the
 * declaration is wrong, which is why this sits beside the type dropdown.
 */
function renderTypeMismatch(row: AdyenDocumentStateRow) {
  const { declaredType, detectedType } = row;
  if (!detectedType || declaredType === detectedType) return null;
  const detected = describeIdentityDocumentType(detectedType);
  return (
    <Tag color="orange" style={{ marginLeft: 8 }}>
      {declaredType
        ? `the file reads as ${detected}`
        : `detected as ${detected}`}
    </Tag>
  );
}

function SubmissionResult({
  result,
}: {
  result: ResubmitAdyenDocumentResponse;
}) {
  const sides = result.sidesSubmitted.length
    ? result.sidesSubmitted.join(" and ")
    : "the document";

  return (
    <Alert
      type={result.outcome === "submitted" ? "success" : "info"}
      showIcon
      message={
        result.outcome === "submitted"
          ? `Sent ${sides} to Adyen`
          : "Adyen already held this document — nothing was sent"
      }
      description={
        <Space direction="vertical" size={2}>
          <span>
            Adyen document{" "}
            <Typography.Text code>{result.documentId}</Typography.Text>
            {result.previousDocumentId
              ? `, replacing ${result.previousDocumentId}`
              : ""}
            {` · declared as ${describeIdentityDocumentType(
              result.declaredType
            )}`}
          </span>
          {result.missingSide && (
            <span>
              No {result.missingSide} side was on file, so it was not sent.
            </span>
          )}
          {result.outcome === "submitted" && (
            <strong>
              Adyen accepted the upload — verification is still pending and the
              verdict arrives later by webhook.
            </strong>
          )}
        </Space>
      }
    />
  );
}

function confirmAcknowledgement({
  acknowledgement,
  reason,
  onConfirm,
  onCancel,
}: {
  acknowledgement: AdyenDocumentAcknowledgement;
  reason: AdyenDocumentReason;
  onConfirm: () => void;
  onCancel?: () => void;
}) {
  Modal.confirm({
    title: ACKNOWLEDGEMENT_TITLE[acknowledgement],
    content: describeAdyenDocumentReason(reason),
    okText: "Send anyway",
    okType: "danger",
    cancelText: "Cancel",
    onOk: onConfirm,
    onCancel,
  });
}
